import { useEffect, useRef, useState } from "react";
import { useUnlockedWallet } from "../wallet";
import { deriveEvmAddress } from "./keys";
import { fetchEvmHoldings, type EvmHoldings } from "./balances";

// Module-scoped cache so navigating between tabs doesn't re-fetch on
// every mount. The hook still re-renders consumers from this snapshot
// while a fresh request is in flight in the background.
const CACHE_TTL_MS = 60_000;
let lastCache: EvmHoldings | null = null;
let lastFetchAt = 0;

/**
 * Wipe the module-level holdings cache. Called from the wallet reset
 * flow so a freshly-created wallet doesn't briefly render the
 * previous user's EVM totals while the new derived address's first
 * fetch is in flight.
 */
export function resetEvmHoldingsCache(): void {
  lastCache = null;
  lastFetchAt = 0;
}

export function useEvmHoldings() {
  const wallet = useUnlockedWallet();
  const [address, setAddress] = useState<`0x${string}` | null>(null);
  const [holdings, setHoldings] = useState<EvmHoldings | null>(lastCache);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inflight = useRef<AbortController | null>(null);

  // Derive address once per session — `deriveEvmAddress` is synchronous
  // but the mnemonic only becomes available after the wallet unlocks.
  useEffect(() => {
    if (!wallet) return;
    try {
      setAddress(deriveEvmAddress(wallet.phrase));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [wallet]);

  const load = async (force = false) => {
    if (!address) return;
    if (!force && lastCache && Date.now() - lastFetchAt < CACHE_TTL_MS) {
      setHoldings(lastCache);
      return;
    }
    inflight.current?.abort();
    const ctl = new AbortController();
    inflight.current = ctl;
    setLoading(true);
    try {
      const h = await fetchEvmHoldings(address, ctl.signal);
      if (ctl.signal.aborted) return;
      lastCache = h;
      lastFetchAt = h.fetchedAt;
      setHoldings(h);
      setError(null);
    } catch (e) {
      if ((e as Error).name === "AbortError") return;
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (!ctl.signal.aborted) setLoading(false);
    }
  };

  useEffect(() => {
    if (!address) return;
    void load();
    // 90s background refresh — short enough that swapping into the
    // wallet tab post-swap surfaces fresh numbers within ~one cycle.
    const t = setInterval(() => void load(), 90_000);
    return () => {
      clearInterval(t);
      inflight.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address]);

  return { address, holdings, loading, error, refresh: () => load(true) };
}
